import React, { useState, useCallback } from 'react'
import _get from 'lodash/get'
import { Stack, Typography, Grid, styled, Paper, Box } from '@mui/material'
import {
  BooleanInput,
  DateTimeInput,
  required,
  TextInput,
  useEditContext,
  Loading,
  useTranslate,
  FormTab,
  useLocaleState,
  maxLength,
  TransformData,
} from 'react-admin'
import { ImageField, MultipleImageField } from '../../components/fields'
import dayjs from 'dayjs'
import { isAfterDate, isBeforeDate, isSameDate, isYoutubeLink } from '../../utils/validators'
import { TabbedMultiCardForm } from '../../components/tab'
import ModifyTimeStamp from '../../components/ModifyTimeStamp'
import { EditBase } from '../../components/base'
// import { CSRTypeSelectInput } from '../../components/domains/CSR/CSRTypeSelectInput'
import { CSRPublishCondSelectInput } from '../../components/domains/csr/CSRPublishCondSelectInput'
import { CSRPublishNote } from '../../components/domains/csr/CSRPublishNote'
// import { CSRDeepLinkInput } from '../../components/domains/CSR/CSRDeepLinkInput'
import {
  ThumbnailInputsProps,
  CoverInputsProps,
  PublishPeriodInputsProps,
  EditApplyEnglishButtonProps,
  ImagesInputsProps,
} from './type'
import { COMMON_FIELDS, CSR_FIELDS } from '../../providers/localization/constant'
import { SaveAndPublishToolBar } from '../../components/form/'
// import { ApproveAndPublishToolBar } from '../../components/form/'
import { MyRichTextInput } from '../../components/text-editor'
import { ImageInput } from '../../components/inputs'
import ApplyToEnglishButton from '../../components/buttons/ApplyToEnglishButton'
import { CoverInput } from '../../components/inputs/CoverInput'
import { ThumbnailInput } from '../../components/inputs/ThumbnailInput'
// import { FilterTypeProjectAutocompleteArrayInput } from '../../components/form/FilterTypeProjectAutocompleteArrayInput'
import sanitizeHtml from 'sanitize-html'
import trim from 'lodash/trim'
import unescape from 'lodash/unescape'

const RESOURCE_PREFIX = 'resources.csr.edit'

const PaperStyled = styled(Paper)(({ theme }) => ({
  ...theme.typography.body2,
  textAlign: 'center',
  color: theme.palette.text.secondary,
  height: 120,
  lineHeight: '60px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& .RaImageField-image': {
    verticalAlign: 'middle',
  },
}))

const CSRThumbnailInputs: React.FC<ThumbnailInputsProps> = ({
  isApproved = false,
  record,
  field,
  source,
  label,
}) => {
  const translate = useTranslate()
  const image = _get(record, field)

  return isApproved ? (
    <>
      <Typography variant='caption' color='common.grey'>
        {translate(label)}
      </Typography>
      <PaperStyled variant='outlined'>
        {image ? (
          <ImageField source={field} title='title' />
        ) : (
          <Typography variant='caption' sx={{ color: 'error.main' }}>
            {translate(`${CSR_FIELDS}.no_image`)}
          </Typography>
        )}
      </PaperStyled>
    </>
  ) : (
    <ThumbnailInput
      source={source}
      label={label}
      accept='image/*'
      fallbackSource={'full_path'}
      initCrop={false}
    >
      <ImageField source='src' title='title' />
    </ThumbnailInput>
  )
}

const CSRCoverInputs: React.FC<CoverInputsProps> = ({
  isApproved = false,
  record,
  field,
  source,
  label,
}) => {
  const translate = useTranslate()
  const image = _get(record, field)

  return isApproved ? (
    <>
      <Typography variant='caption' color='common.grey'>
        {translate(label)}
      </Typography>
      <PaperStyled variant='outlined'>
        {image ? (
          <ImageField source={field} title='title' />
        ) : (
          <Typography variant='caption' sx={{ color: 'error.main' }}>
            {translate(`${CSR_FIELDS}.no_image`)}
          </Typography>
        )}
      </PaperStyled>
    </>
  ) : (
    <CoverInput
      source={source}
      label={label}
      accept='image/*'
      fallbackSource={'full_path'}
      initCrop={false}
    >
      <ImageField source='src' title='title' />
    </CoverInput>
  )
}

const CSRMultipleImagesInputs: React.FC<ImagesInputsProps> = ({
  isApproved = false,
  record,
  source,
  label,
}) => {
  const translate = useTranslate()
  const image = _get(record, source)

  return isApproved ? (
    <>
      <Typography variant='caption' color='common.grey'>
        {translate(label)}
      </Typography>
      <Box>
        {image ? (
          <MultipleImageField source={source} title='name' target='_blank' />
        ) : (
          <PaperStyled variant='outlined'>
            <Typography variant='caption' sx={{ color: 'error.main' }}>
              {translate(`${CSR_FIELDS}.no_image`)}
            </Typography>
          </PaperStyled>
        )}
      </Box>
    </>
  ) : (
    <ImageInput
      source='images'
      label={label}
      accept='image/*'
      multiple
      initCrop={false}
      fallbackSource='full_path'
      helperText={`${CSR_FIELDS}.images_helper`}
      deleteFileField='delete_images'
    ></ImageInput>
  )
}

const CustomToolbar: React.FC = () => {
  return (
    <SaveAndPublishToolBar encType='multipart/form-data'>
      <CSRPublishNote />
    </SaveAndPublishToolBar>
  )
}

const PublishPeriodInputs: React.FC<PublishPeriodInputsProps> = ({ isApproved = false }) => {
  const defaultPublishDate = dayjs()
  const defaultExpiredDate = defaultPublishDate.add(2, 'day')

  return (
    <>
      <DateTimeInput
        source='start_date'
        label={`${COMMON_FIELDS}.date.published_on`}
        disabled={isApproved}
        fullWidth
        defaultValue={defaultPublishDate}
        validate={[
          required(),
          isBeforeDate('end_date', 'The publish date must be in before the expired date.'),
        ]}
      />
      <DateTimeInput
        source='end_date'
        disabled={isApproved}
        label={`${COMMON_FIELDS}.date.expired_on`}
        fullWidth
        defaultValue={defaultExpiredDate}
        validate={[
          required(),
          isAfterDate('start_date', 'The expired date must be in after the publish date.'),
          !isApproved ? isSameDate() : null,
        ]}
      />
    </>
  )
}

const EditApply: React.FC<EditApplyEnglishButtonProps> = ({ isApproved, updateKeyEditor }) => {
  return !isApproved ? (
    <Grid item xs={12}>
      <ApplyToEnglishButton updateKeyEditor={updateKeyEditor} />
    </Grid>
  ) : (
    <></>
  )
}

const PersistForm: React.FC<{ preventEdit: boolean }> = ({ preventEdit = false }) => {
  const [activeLocale] = useLocaleState()
  const { record } = useEditContext()

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          {/* <Grid item xs={6}>
            <CSRTypeSelectInput disabled={preventEdit} />
          </Grid> */}
          <Grid item xs={6}>
            <CSRPublishCondSelectInput disabled={preventEdit} />
          </Grid>
        </Grid>
      </Grid>
      {/* <Grid item xs={12}>
        <Grid container spacing={2}>
          <FilterTypeProjectAutocompleteArrayInput
            source='property_ids'
            depField='publish_condition'
            expectedValue='s'
            label='Select Project'
            disabled={preventEdit}
          />
        </Grid>
      </Grid>  */}
      <Grid item xs={12} order={{ xs: -1, sm: 0 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
              <PublishPeriodInputs isApproved={preventEdit} />
            </Stack>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          {/* <Grid item xs={12}>
            <CSRDeepLinkInput disabled={preventEdit} buttonProps={{ sx: { mt: 0.5 } }} />
          </Grid> */}
          <Grid item xs={12}>
            <TextInput
              source='youtube_link'
              label='Youtube Link'
              InputProps={{
                fullWidth: true,
              }}
              fullWidth
              validate={[isYoutubeLink()]}
              disabled={preventEdit}
            />
          </Grid>
          <Grid item xs={6}>
            <TextInput
              source='slug_th'
              label={`${COMMON_FIELDS}.slug_th.label`}
              InputProps={{
                fullWidth: true,
              }}
              fullWidth
              disabled={preventEdit}
              validate={[required()]}
            />
          </Grid>
          <Grid item xs={6}>
            <TextInput
              source='slug_en'
              label={`${COMMON_FIELDS}.slug_en.label`}
              InputProps={{
                fullWidth: true,
              }}
              fullWidth
              disabled={preventEdit}
              validate={[required()]}
            />
          </Grid>
          <Grid item sm={6}>
            <BooleanInput
              source='is_highlight'
              label={`${COMMON_FIELDS}.highlight.label`}
              sx={{
                '& .MuiFormControlLabel-root': { marginRight: 'auto' },
              }}
              disabled={preventEdit}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <CSRCoverInputs
          isApproved={preventEdit}
          record={record}
          source='cover'
          field='cover.full_path'
          label={`${CSR_FIELDS}.cover`}
        />
      </Grid>
      <Grid item xs={12}>
        <CSRThumbnailInputs
          isApproved={preventEdit}
          record={record}
          source='thumbnail'
          field='thumbnail.full_path'
          label={`${CSR_FIELDS}.thumbnail_path_helper`}
        />
      </Grid>
      <Grid item xs={12}>
        <CSRMultipleImagesInputs
          source='images'
          isApproved={preventEdit}
          record={record}
          label={`${CSR_FIELDS}.images`}
        />
      </Grid>
      {/* <Grid item xs={12}>
        <MultipleFileInput
          source='files'
          isApproved={preventEdit}
          label={`${CSR_FIELDS}.attachment`}
          helperText={`${CSR_FIELDS}.attachment_helper`}
          deleteFileField='delete_files'
          fallbackSource='full_path'
          accept='application/pdf'
        />
      </Grid> */}
      <Grid item xs={12}>
        <ModifyTimeStamp language={activeLocale} />
      </Grid>
    </Grid>
  )
}

const EditForm: React.FC = () => {
  const { record, isLoading } = useEditContext()
  const [editorContentEnKey, setEditorContentEnKey] = useState(0)
  // const isApproved: boolean = record?.is_approved
  const isPublished: boolean = record?.is_published
  const preventEdit = isPublished
  const reloadTextEditor = (): void => {
    setEditorContentEnKey(editorContentEnKey + 1)
  }
  const translate = useTranslate()
  if (isLoading) return <Loading />

  return (
    <TabbedMultiCardForm
      formProps={{ toolbar: null, toolbarBottom: <CustomToolbar /> }}
      childrenForms={[<PersistForm key='' preventEdit={preventEdit} />]}
    >
      <FormTab label='TH'>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextInput
              source='title_th'
              label={`${CSR_FIELDS}.title_th`}
              InputProps={{
                fullWidth: true,
              }}
              fullWidth
              validate={[
                required(),
                maxLength(
                  50,
                  `${translate(`${CSR_FIELDS}.title_th`)} ต้องมีความยาวไม่เกิน 50 ตัวอักษร`,
                ),
              ]}
              disabled={preventEdit}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInput
              source='short_description_th'
              label={`${CSR_FIELDS}.short_description_th`}
              InputProps={{
                fullWidth: true,
              }}
              fullWidth
              multiline
              rows={3}
              validate={[
                required(),
                maxLength(
                  150,
                  `${translate(
                    `${CSR_FIELDS}.short_description_th`,
                  )} ต้องมีความยาวไม่เกิน 150 ตัวอักษร`,
                ),
              ]}
              disabled={preventEdit}
            />
          </Grid>
          <Grid item xs={12}>
            <MyRichTextInput
              fullWidth
              label={`${CSR_FIELDS}.description_th`}
              source='description_th'
              validate={[required()]}
              disabled={preventEdit}
            />
          </Grid>
          <EditApply isApproved={preventEdit} updateKeyEditor={reloadTextEditor} />
        </Grid>
      </FormTab>
      <FormTab label='EN'>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextInput
              source='title_en'
              label={`${CSR_FIELDS}.title_en`}
              InputProps={{
                fullWidth: true,
              }}
              fullWidth
              validate={[
                required(),
                maxLength(
                  50,
                  `${translate(`${CSR_FIELDS}.title_en`)} ต้องมีความยาวไม่เกิน 50 ตัวอักษร`,
                ),
              ]}
              disabled={preventEdit}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInput
              source='short_description_en'
              label={`${CSR_FIELDS}.short_description_en`}
              InputProps={{
                fullWidth: true,
              }}
              fullWidth
              multiline
              rows={3}
              validate={[
                required(),
                maxLength(
                  150,
                  `${translate(
                    `${CSR_FIELDS}.short_description_en`,
                  )} ต้องมีความยาวไม่เกิน 150 ตัวอักษร`,
                ),
              ]}
              disabled={preventEdit}
            />
          </Grid>
          <Grid item xs={12}>
            <MyRichTextInput
              fullWidth
              label={`${CSR_FIELDS}.description_en`}
              source='description_en'
              validate={[required()]}
              disabled={preventEdit}
            />
          </Grid>
        </Grid>
      </FormTab>
    </TabbedMultiCardForm>
  )
}

const CSREdit: React.FC = () => {
  const transform: TransformData = useCallback((data) => {
    return {
      ...data,
      title_th: sanitizeHtml(unescape(data.title_th)),
      title_en: sanitizeHtml(unescape(data.title_en)),
      short_description_th: sanitizeHtml(unescape(data.short_description_th)),
      short_description_en: sanitizeHtml(unescape(data.short_description_en)),
      description_th: sanitizeHtml(unescape(data.description_th)),
      description_en: sanitizeHtml(unescape(data.description_en)),
      slug_th: trim(data.slug_th),
      slug_en: trim(data.slug_en),
    }
  }, [])
  return (
    <EditBase
      title={`${RESOURCE_PREFIX}.title`}
      listAction={{
        hasBackButton: true,
      }}
      noCard
      encType='multipart/form-data'
      transform={transform}
    >
      <EditForm />
    </EditBase>
  )
}

export default CSREdit
